import { useState } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon, Download } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Status } from "@/types";
import { usePackageStore } from "@/stores/packageStore";
import { toast } from "@/components/ui/sonner";

interface GenerateReportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const GenerateReportModal = ({ 
  open, 
  onOpenChange 
}: GenerateReportModalProps) => {
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [selectedStatuses, setSelectedStatuses] = useState<Status[]>([
    "Pending", "Delivered", "Deleted", "Discarded"
  ]);
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  
  const { packages } = usePackageStore();
  
  const handleToggleStatus = (status: Status) => {
    if (selectedStatuses.includes(status)) {
      setSelectedStatuses(selectedStatuses.filter(s => s !== status));
    } else {
      setSelectedStatuses([...selectedStatuses, status]);
    }
  };
  
  const handleGenerateReport = () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    
    // Filter packages based on selected dates and statuses
    const filteredPackages = packages.filter(pkg => {
      const pkgDate = new Date(pkg.entry_date);
      return (
        pkgDate >= startDate &&
        pkgDate <= endDate &&
        selectedStatuses.includes(pkg.status)
      );
    });
    
    if (filteredPackages.length === 0) {
      toast.error("No packages found for the selected criteria");
      return;
    }
    
    // In a real implementation, this would generate a PDF
    // For this example, we'll just simulate it
    toast.success(`Report generated with ${filteredPackages.length} packages`);
    
    // Simulate PDF download
    setTimeout(() => {
      const filename = `package-report-${format(startDate, 'yyyy-MM-dd')}-to-${format(endDate, 'yyyy-MM-dd')}.pdf`;
      const element = document.createElement('a');
      element.setAttribute('href', 'data:application/pdf;charset=utf-8,');
      element.setAttribute('download', filename);
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
      
      onOpenChange(false);
    }, 1000);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" aria-describedby="generate-report-modal-desc">
        <span id="generate-report-modal-desc" style={{ display: 'none' }}>
          Generate a report for packages within a selected date range.
        </span>
        <DialogHeader>
          <DialogTitle>Generate Package Report</DialogTitle>
          <DialogDescription>
            Select date range and package statuses to include in the report
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 pt-4">
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Date Range</h3>
            <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
              <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex justify-start w-full sm:w-[140px]"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, 'PP') : 'Start date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => {
                      setStartDate(date);
                      setStartDateOpen(false);
                    }}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
              
              <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex justify-start w-full sm:w-[140px]"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, 'PP') : 'End date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={(date) => {
                      setEndDate(date);
                      setEndDateOpen(false);
                    }}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                    disabled={(date) => startDate ? date < startDate : false}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Package Statuses</h3>
            <div className="flex flex-wrap gap-2">
              {(["Pending", "Delivered", "Deleted", "Discarded"] as Status[]).map(status => (
                <Button
                  key={status}
                  variant={selectedStatuses.includes(status) ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleToggleStatus(status)}
                  className="flex-1"
                >
                  {status}
                </Button>
              ))}
            </div>
          </div>
        </div>
        
        <DialogFooter className="flex justify-between pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerateReport}
            className="flex items-center"
          >
            <Download className="mr-2 h-4 w-4" />
            Generate Report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GenerateReportModal;
